<script setup lang="ts">
import type { PropType } from 'vue'
import type { Category, Tag } from '@/models/content'
import IconLock from '@/assets/icons/lock.svg'
import IconPlay from '@/assets/icons/play.svg'

const { $modal } = useNuxtApp()

const props = defineProps({
  slug: {
    type: [String, Number],
    required: true,
  },
  publishedDate: {
    type: String,
    required: true,
  },
  duration: {
    type: Number,
    required: true,
  },
  title: {
    type: String,
    required: true,
  },
  excerpt: {
    type: String,
    required: true,
  },
  pills: {
    type: Array as PropType<Tag[] | Category[]>,
    required: true,
  },
  hasSubscription: {
    type: Boolean,
    default: false,
  },
  hasAuthenticated: {
    type: Boolean,
    default: false,
  },
  isPreview: {
    type: Boolean,
    default: false,
  },
})

const isMobile = useMobile()

const isAuthenticated = computed(() => props.hasAuthenticated)
const isAuthorized = computed(() => props.hasSubscription)

const isPreviewVideo = computed(() => {
  if (isAuthorized.value) {
    return false
  }
  return props.isPreview
})
const textButton = computed(() => {
  return isPreviewVideo.value ? ' Watch Preview' : ' Watch Now'
})

const formattedDuration = useFormattedDuration('video', props.duration)
const emit = defineEmits(['playVideo'])
const handleButton = () => {
  if (isAuthorized.value || isPreviewVideo.value) {
    emit('playVideo')
    return
  }
  $modal.open('requestATrial')
}
const handleModal = () => {
  if (isAuthenticated.value && !isAuthorized.value) {
    if (isPreviewVideo.value) {
      $modal.open('subscribe')
    } else {
      $modal.open('requestATrial')
    }
    return
  }
  $modal.open('login')
}
</script>

<template>
  <article class="bg-white py-6 md:bg-opacity-0 md:py-0">
    <p class="text-xs font-semibold text-s-500 md:text-white">
      {{ publishedDate }} · {{ formattedDuration }}
    </p>

    <h1
      data-testid="video-card-title"
      class="mt-2 line-clamp-3 max-w-[560px] text-xl font-bold md:font-inter md:text-3xl"
    >
      {{ title }}
    </h1>

    <div class="mb-5 mt-3 hidden h-line-4 w-16 bg-p-500 md:block" />

    <div class="mb-4 mt-3 max-w-[560px] md:mt-0">
      <div
        class="custom-line-clamp-lg text-base md:font-semibold"
        v-html="excerpt"
      />
    </div>

    <div class="mt-4 flex gap-2">
      <LinkedPill
        v-for="pill in pills"
        :key="pill.id"
        :type="pill.__typename"
        :slug="pill.slug"
        :variant="isMobile ? 'black' : 'white'"
      >
        {{ pill.name }}
      </LinkedPill>
    </div>

    <div class="mt-11 hidden flex-col gap-2 md:flex">
      <div
        v-if="!isAuthorized && !isPreviewVideo"
        class="flex flex-row items-baseline gap-x-2 text-2xl font-bold text-white"
      >
        <IconLock class="h-[24px] w-[24px] text-p-500" />
        <span>Member Only Content</span>
      </div>
      <button
        class="btn primary-btn flex max-w-[160px] items-center justify-center rounded px-4 py-2 text-xs font-semibold text-s-900"
        data-testid="button-play-video"
        @click="handleButton"
      >
        <span
          v-if="isAuthorized || isPreviewVideo"
          class="flex flex-row items-center justify-center gap-x-2"
        >
          <IconPlay class="h-5 w-5" />
          {{ textButton }}
        </span>
        <span v-else>Request a Trial</span>
      </button>
      <div
        v-if="!isAuthenticated"
        class="flex gap-1 text-xs"
        data-test="authorized-container"
      >
        <span class="text-xs">Already a Subscriber?</span>
        <span class="cursor-pointer font-bold text-p-500" @click="handleModal">
          Sign In
        </span>
      </div>
      <div
        v-if="isAuthenticated && !isAuthorized && isPreviewVideo"
        class="flex gap-1 text-xs"
        data-test="authorized-container"
      >
        <span class="cursor-pointer font-bold text-p-500" @click="handleModal">
          Subscribe
        </span>
        <span class="text-xs">to watch the full video</span>
      </div>
    </div>
  </article>
</template>
